import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { AUTH_URL } from '../config';

const AuthContext = createContext(null);

// Role constants
export const ROLES = {
  SUPER: 'super',
  EMPLOYER: 'employer',
  EMPLOYEE: 'employee',
  ADMIN: 'admin'
};

// Single source of truth for feature access. Keys are feature areas checked by
// route guards (App.js) and sidebar visibility (Sidebar.js) via hasPermission.
// 'corps' is intentionally absent: only super reaches it, through '*'.
export const ROLE_PERMISSIONS = {
  'super': ['*'], // Access to everything
  'admin': ['admin', 'order', 'employees', 'invoices', 'timesheets', 'clients', 'vendors', 'timesheetItems', 'expenses', 'documents'],
  'employer': ['employees', 'invoices', 'timesheets', 'timesheetItems', 'expenses', 'documents'],
  'employee': ['timesheets','timesheetItems', 'expenses', 'documents']
};

const parseJwt = (token) => {
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch (e) {
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => localStorage.getItem('jwtToken'));
  const [user, setUser] = useState(() => {
    const t = localStorage.getItem('jwtToken');
    return t ? parseJwt(t) : null;
  });
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (token) {
      setUser(parseJwt(token));
      localStorage.setItem('jwtToken', token);
    } else {
      setUser(null);
      localStorage.removeItem('jwtToken');
    }
    setIsInitialized(true);
  }, [token]);

  const login = useCallback(async (username, password) => {
    // Configurable client credentials
    //const CLIENT_ID = 'demo_client';
    //const CLIENT_SECRET = 'demo_secret';
    const BASIC_AUTH = 'Basic ' + btoa(`${username}:${password}`);

    const params = new URLSearchParams();
    params.append('grant_type', 'client_credentials');
//    params.append('username', username);
//    params.append('password', password);
//    params.append('client_id', CLIENT_ID);
//    params.append('client_secret', CLIENT_SECRET);
    let response;
    try {
      response = await fetch(`${AUTH_URL}/oauth2/token`, {
        method: 'POST',
        headers: {
          'Authorization': BASIC_AUTH,
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: params
      });
    } catch (e) {
      // fetch itself failed: server unreachable, DNS, CORS, offline.
      throw new Error('Unable to reach the server. Check your connection and try again.');
    }
    if (!response.ok) {
      if (response.status === 401 || response.status === 400) {
        throw new Error('Incorrect email or password.');
      }
      let detail = null;
      try {
        const body = await response.json();
        detail = body && (body.error_description || body.error || body.message);
      } catch (e) { /* non-JSON body */ }
      throw new Error(detail || 'Sign in failed. Please try again.');
    }
    const data = await response.json();
    setToken(data.access_token);
    return data;
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('jwtToken');
    window.localStorage.clear();
    window.sessionStorage && window.sessionStorage.clear();
  }, []);

  const getUserRoles = useCallback(() => {
    if (!user) return [];
    // authorities: ["ROLE_SUPER", "ROLE_EMPLOYER", "ROLE_ADMIN"]
    if (user.authorities) {
      return user.authorities.map(a => a.replace(/^ROLE_/, '').toLowerCase());
    }
    return [];
  }, [user]);

  const hasRole = useCallback((role) => {
    return getUserRoles().includes(role.toLowerCase());
  }, [getUserRoles]);

  const hasAnyRole = useCallback((roles) => {
    return roles.some(role => hasRole(role));
  }, [hasRole]);

  const hasAllRoles = useCallback((roles) => {
    return roles.every(role => hasRole(role));
  }, [hasRole]);

  const isSuperUser = useCallback(() => {
    return hasRole(ROLES.SUPER);
  }, [hasRole]);

  const hasPermission = useCallback((permission) => {
    if (isSuperUser()) return true;
    return getUserRoles().some(role =>
      ROLE_PERMISSIONS[role]?.includes(permission) ||
      ROLE_PERMISSIONS[role]?.includes('*')
    );
  }, [getUserRoles, isSuperUser]);

  const value = {
    token,
    user,
    isInitialized,
    login,
    logout,
    getUserRoles,
    hasRole,
    hasAnyRole,
    hasAllRoles,
    isSuperUser,
    hasPermission
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Higher Order Component for role-based access control
export const withRole = (WrappedComponent, requiredPermissions) => {
  return function WithRoleComponent(props) {
    const { hasPermission, isSuperUser, isInitialized, user } = useAuth();
    if (!isInitialized || !user) {
      return <div>Please log in to access this content.</div>;
    }
    if (isSuperUser()) {
      return <WrappedComponent {...props} />;
    }
    const hasAccess = requiredPermissions.every(permission => hasPermission(permission));
    if (!hasAccess) {
      return <div>You don't have permission to access this content.</div>;
    }
    return <WrappedComponent {...props} />;
  };
}; 